<template>
  <transition name="fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 bg-slate-950/70 backdrop-blur-md z-[10001] flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
      @click.self="$emit('update:modelValue', false)"
    >
      <div
        ref="modalRef"
        class="bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl border border-slate-200/90 dark:border-[#263449] relative flex flex-col transform transition-all duration-300"
      >
        <!-- Top Subtle Gradient Accent Line -->
        <div class="h-1 bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 shrink-0"></div>

        <!-- Professional Modal Header Bar -->
        <div
          class="bg-white dark:bg-[#111C30] px-6 py-4 border-b border-slate-100 dark:border-[#263449] flex items-center justify-between shrink-0"
        >
          <div>
            <div class="flex items-center gap-2.5 flex-wrap">
              <h3 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
                {{ userName || "Staff" }}'s Leave Balance
              </h3>
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/50"
              >
                {{ selectedYear }}
              </span>
            </div>
            <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-normal">
              Annual leave allocations, usage metrics, and monthly records
            </p>
          </div>

          <div class="flex items-center gap-2.5">
            <!-- Year Selector Pill -->
            <div
              class="flex items-center gap-1.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] px-2.5 py-1.5 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-300"
            >
              <Calendar class="w-3.5 h-3.5 text-slate-400" />
              <span class="hidden sm:inline">Year:</span>
              <input
                v-model.number="selectedYear"
                type="number"
                min="2000"
                :max="new Date().getFullYear() + 2"
                class="w-14 bg-transparent text-slate-900 dark:text-white font-semibold focus:outline-none text-center"
              />
            </div>

            <!-- Close Button -->
            <button
              @click="$emit('update:modelValue', false)"
              class="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors"
              title="Close"
            >
              <X class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Scrollable Modal Content -->
        <div class="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6 bg-slate-50/50 dark:bg-[#0B1120]">
          <!-- Loading State -->
          <div
            v-if="isLoading"
            class="rounded-2xl p-8 bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] flex items-center justify-center min-h-[320px]"
          >
            <AppLoader
              variant="card"
              size="md"
              message="Loading Leave Records..."
              subtext="Aggregating annual quotas, monthly usage, and balances"
              color="indigo"
            />
          </div>

          <!-- Main Content (when not loading) -->
          <template v-else-if="leaveBalance && leaveBalance.balances?.length">
            <!-- Top Summary KPI Cards (Matching Reference Image) -->
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <!-- Total Quota -->
              <div
                class="bg-white dark:bg-[#111C30] p-4 rounded-xl sm:rounded-2xl border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <div class="p-2 bg-blue-50 dark:bg-blue-950/40 rounded-xl text-blue-600 dark:text-blue-400">
                      <Layers class="w-5 h-5" />
                    </div>
                    <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
                      Quota
                    </span>
                  </div>
                  <h4 class="text-sm font-bold text-slate-800 dark:text-slate-100">
                    Total Quota
                  </h4>
                  <p class="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
                    {{ totalAllocation }}
                  </p>
                </div>
                <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">
                  Allocated days
                </p>
              </div>

              <!-- Consumed -->
              <div
                class="bg-white dark:bg-[#111C30] p-4 rounded-xl sm:rounded-2xl border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <div class="p-2 bg-amber-50 dark:bg-amber-950/40 rounded-xl text-amber-600 dark:text-amber-400">
                      <Clock class="w-5 h-5" />
                    </div>
                    <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
                      Used
                    </span>
                  </div>
                  <h4 class="text-sm font-bold text-slate-800 dark:text-slate-100">
                    Consumed
                  </h4>
                  <p class="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
                    {{ totalUsed }}
                  </p>
                </div>
                <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">
                  Leaves utilized
                </p>
              </div>

              <!-- Available -->
              <div
                class="bg-white dark:bg-[#111C30] p-4 rounded-xl sm:rounded-2xl border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <div class="p-2 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl text-emerald-600 dark:text-emerald-400">
                      <CheckCircle class="w-5 h-5" />
                    </div>
                    <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
                      Balance
                    </span>
                  </div>
                  <h4 class="text-sm font-bold text-slate-800 dark:text-slate-100">
                    Available
                  </h4>
                  <p
                    class="text-2xl sm:text-3xl font-extrabold mt-1 tracking-tight"
                    :class="totalRemaining === 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'"
                  >
                    {{ totalRemaining }}
                  </p>
                </div>
                <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">
                  Remaining days
                </p>
              </div>

              <!-- Utilization Rate -->
              <div
                class="bg-white dark:bg-[#111C30] p-4 rounded-xl sm:rounded-2xl border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <div class="p-2 bg-blue-50 dark:bg-blue-950/40 rounded-xl text-blue-600 dark:text-blue-400">
                      <Percent class="w-5 h-5" />
                    </div>
                    <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
                      Ratio
                    </span>
                  </div>
                  <h4 class="text-sm font-bold text-slate-800 dark:text-slate-100">
                    Utilization
                  </h4>
                  <p class="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
                    {{ utilizationPercentage }}%
                  </p>
                </div>
                <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">
                  Of annual quota
                </p>
              </div>
            </div>

            <!-- ===== MONTHLY LEAVE USAGE CHART ===== -->
            <div
              class="bg-white dark:bg-[#111C30] rounded-2xl p-5 sm:p-6 border border-slate-200/80 dark:border-[#263449] shadow-xs"
            >
              <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div class="flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                    <BarChart3 class="w-4 h-4" />
                  </div>
                  <div>
                    <h4 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                      Monthly Leave Distribution
                    </h4>
                    <p class="text-[11px] text-slate-500 dark:text-slate-400">
                      Breakdown of leaves utilized across calendar months
                    </p>
                  </div>
                </div>

                <!-- Legend pills summary -->
                <div class="flex items-center gap-2 flex-wrap text-xs">
                  <span
                    v-for="(color, type) in activeLeaveTypes"
                    :key="type"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium bg-slate-50 dark:bg-slate-800/80 text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/60"
                  >
                    <span class="w-2 h-2 rounded-full" :style="{ backgroundColor: color }"></span>
                    {{ type }}
                  </span>
                </div>
              </div>

              <!-- Chart Container -->
              <div class="relative h-[280px] sm:h-[320px] w-full">
                <canvas ref="chartCanvas" class="w-full h-full"></canvas>
              </div>
            </div>

            <!-- ===== LEAVE TYPE BREAKDOWN CARDS ===== -->
            <div>
              <div class="flex items-center justify-between mb-3.5">
                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Allocated Leave Categories ({{ leaveBalance.balances.length }})
                </h4>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div
                  v-for="(item, index) in leaveBalance.balances"
                  :key="index"
                  class="bg-white dark:bg-[#111C30] rounded-2xl p-5 border border-slate-200/80 dark:border-[#263449] shadow-xs hover:shadow-md transition-all duration-200 relative group"
                >
                  <!-- Card Header -->
                  <div class="flex items-start justify-between mb-4">
                    <div class="flex items-center gap-2.5">
                      <div
                        class="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm shadow-xs"
                        :style="{
                          backgroundColor: `${getColorForLeaveType(item.type, index)}15`,
                          color: getColorForLeaveType(item.type, index),
                        }"
                      >
                        {{ item.type?.charAt(0) || "L" }}
                      </div>
                      <div>
                        <h5 class="text-sm font-bold text-slate-900 dark:text-white">
                          {{ item.type }}
                        </h5>
                        <p class="text-[11px] text-slate-400 dark:text-slate-500">
                          Total: {{ item.total }} days
                        </p>
                      </div>
                    </div>

                    <div class="flex items-center gap-1.5">
                      <!-- Remaining Badge -->
                      <span
                        class="px-2.5 py-1 rounded-xl text-xs font-bold border"
                        :class="
                          item.remaining === 0
                            ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-900/60'
                            : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/60'
                        "
                      >
                        {{ item.remaining }} Left
                      </span>

                      <!-- Admin Actions Menu -->
                      <div
                        v-if="userRole === 'admin' || userRole === 'superadmin'"
                        class="relative"
                      >
                        <button
                          @click.stop="toggleMenu(index)"
                          class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                          title="Options"
                        >
                          <MoreVertical class="w-4 h-4" />
                        </button>

                        <!-- Dropdown -->
                        <div
                          v-if="activeMenu === index"
                          class="absolute right-0 mt-1 w-36 bg-white dark:bg-[#18243A] rounded-xl shadow-xl border border-slate-200 dark:border-[#263449] z-50 overflow-hidden py-1"
                        >
                          <button
                            @click="openEditModal(item); closeMenu();"
                            class="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 hover:text-indigo-600 transition"
                          >
                            <Edit3 class="w-3.5 h-3.5" />
                            Update Quota
                          </button>
                          <button
                            @click="requestDeleteLeaveType(item); closeMenu();"
                            class="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/50 transition border-t border-slate-100 dark:border-slate-800"
                          >
                            <Trash2 class="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Usage Metrics -->
                  <div class="space-y-2">
                    <div class="flex justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
                      <span>Used: <strong class="text-slate-800 dark:text-slate-200">{{ item.used }}</strong></span>
                      <span>
                        {{ item.total > 0 ? Math.round((item.used / item.total) * 100) : 0 }}% used
                      </span>
                    </div>

                    <!-- Progress Track -->
                    <div class="w-full h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        class="h-full rounded-full transition-all duration-700"
                        :style="{
                          width: `${item.total > 0 ? Math.min((item.used / item.total) * 100, 100) : 0}%`,
                          backgroundColor: getColorForLeaveType(item.type, index),
                        }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- No Policy Fallback -->
          <div
            v-else
            class="text-center py-16 px-4 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449]"
          >
            <div
              class="w-16 h-16 bg-indigo-50 dark:bg-indigo-950/60 rounded-2xl flex items-center justify-center mx-auto mb-4 text-indigo-600 dark:text-indigo-400 shadow-xs"
            >
              <Calendar class="w-8 h-8" />
            </div>

            <h4 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white mb-1.5">
              No Leave Policy Configured
            </h4>

            <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto mb-6">
              Create a leave policy in school settings to allocate quotas and start tracking leave usage for {{ selectedYear }}.
            </p>

            <router-link
              v-if="userRole === 'admin' || userRole === 'superadmin'"
              :to="{
                path: '/schoolDetails',
                query: { openPolicy: 'true' },
              }"
              class="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-xs transition shadow-sm"
            >
              <Plus class="w-4 h-4" />
              Configure Leave Policy
            </router-link>
          </div>
        </div>

        <!-- Edit Total Modal -->
        <div
          v-if="showEditModal && editingBalance"
          class="fixed inset-0 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center z-[10002] p-4"
          @click.self="showEditModal = false"
        >
          <div
            class="bg-white dark:bg-[#111C30] rounded-2xl w-full max-w-md shadow-2xl border border-slate-200/90 dark:border-[#263449] overflow-hidden"
          >
            <!-- Header -->
            <div class="px-6 py-4.5 border-b border-slate-100 dark:border-[#263449] bg-slate-50/70 dark:bg-[#0F172A] flex items-center justify-between">
              <div class="flex items-center gap-2.5">
                <div class="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                  <Edit3 class="w-4 h-4" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-slate-900 dark:text-white">
                    Update Leave Quota
                  </h3>
                  <p class="text-[11px] text-slate-500 dark:text-slate-400">
                    {{ userName }} • {{ editingBalance.type }}
                  </p>
                </div>
              </div>
              <button
                @click="showEditModal = false"
                class="text-slate-400 hover:text-slate-600 dark:hover:text-white p-1 rounded-lg"
              >
                <X class="w-4 h-4" />
              </button>
            </div>

            <!-- Body -->
            <div class="p-6 space-y-4">
              <div>
                <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  New Total Allowed Days <span class="text-rose-500">*</span>
                </label>
                <input
                  v-model.number="editingBalance.newTotal"
                  type="number"
                  min="0"
                  class="w-full px-4 py-2.5 border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 text-base font-semibold transition"
                />
                <div class="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mt-2">
                  <span>Current Quota: <strong>{{ editingBalance.total }}</strong></span>
                  <span>Already Consumed: <strong class="text-amber-600 dark:text-amber-400">{{ editingBalance.used }}</strong></span>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="flex justify-end gap-2.5 px-6 py-4 border-t border-slate-100 dark:border-[#263449] bg-slate-50/70 dark:bg-[#0F172A]">
              <button
                @click="showEditModal = false"
                class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition"
              >
                Cancel
              </button>

              <button
                @click="submitEditBalance"
                class="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-sm transition"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>

        <!-- Confirm Deletion Modal -->
        <ConfirmModal
          :show="showConfirmModal"
          :title="confirmConfig.title"
          :description="confirmConfig.description"
          :buttonText="confirmConfig.buttonText"
          :isDestructive="confirmConfig.isDestructive"
          :isLoading="confirmLoading"
          @cancel="showConfirmModal = false"
          @confirm="confirmConfig.action"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
  import {
    ref,
    watch,
    computed,
    reactive,
    onMounted,
    onBeforeUnmount,
    nextTick,
  } from "vue";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import ConfirmModal from "@/components/Students/ConfirmationPopupModal.vue";
  import {
    Calendar,
    MoreVertical,
    Edit3,
    Trash2,
    BarChart3,
    X,
    Layers,
    Clock,
    CheckCircle,
    Percent,
    Plus,
  } from "lucide-vue-next";
  import Chart from "chart.js/auto";
  import store from "@/store";

  const props = defineProps({
    modelValue: Boolean,
    userId: {
      type: String,
      default: null,
    },
    userName: {
      type: String,
      default: "",
    },
  });

  defineEmits(["update:modelValue"]);

  const { showToast } = useGlobalToast();

  const selectedYear = ref(new Date().getFullYear());
  let chartInstance = null;
  const chartCanvas = ref(null);
  const modalRef = ref(null);

  const isLoading = ref(false);
  const showEditModal = ref(false);
  const showConfirmModal = ref(false);
  const confirmLoading = ref(false);
  const activeMenu = ref(null);

  const leaveBalance = computed(() => {
    const data =
      store.state.userLeaveBalances?.[props.userId]?.[selectedYear.value];
    return isLoading.value ? null : data ?? null;
  });

  const userRole = computed(() => store.state.userRole);

  /* ============================= */
  /* AGGREGATED KPIS */
  /* ============================= */
  const totalAllocation = computed(() => {
    if (!leaveBalance.value?.balances?.length) return 0;
    return leaveBalance.value.balances.reduce((acc, b) => acc + (Number(b.total) || 0), 0);
  });

  const totalUsed = computed(() => {
    if (!leaveBalance.value?.balances?.length) return 0;
    return leaveBalance.value.balances.reduce((acc, b) => acc + (Number(b.used) || 0), 0);
  });

  const totalRemaining = computed(() => {
    if (!leaveBalance.value?.balances?.length) return 0;
    return leaveBalance.value.balances.reduce((acc, b) => acc + (Number(b.remaining) || 0), 0);
  });

  const utilizationPercentage = computed(() => {
    if (totalAllocation.value === 0) return 0;
    return Math.round((totalUsed.value / totalAllocation.value) * 100);
  });

  /* ============================= */
  /* CONFIRM CONFIG */
  /* ============================= */
  const confirmConfig = reactive({
    title: "",
    description: "",
    buttonText: "Confirm",
    isDestructive: false,
    action: null,
  });

  /* ============================= */
  /* MENU HANDLING */
  /* ============================= */
  const toggleMenu = (index) => {
    activeMenu.value = activeMenu.value === index ? null : index;
  };

  const closeMenu = () => {
    activeMenu.value = null;
  };

  /* ============================= */
  /* EDIT BALANCE STATE */
  /* ============================= */
  const editingBalance = ref(null);

  const openEditModal = (item) => {
    editingBalance.value = {
      ...item,
      newTotal: item.total,
    };
    showEditModal.value = true;
  };

  /* ============================= */
  /* FETCH BALANCE */
  /* ============================= */
  const fetchBalance = async () => {
    if (!props.userId) return;
    isLoading.value = true;
    try {
      await store.dispatch("fetchUserLeaveBalance", {
        userId: props.userId,
        year: selectedYear.value,
      });

      await nextTick();
      updateChart();
    } catch (err) {
      console.error("Failed to fetch leave balance", err);
    } finally {
      isLoading.value = false;
    }
  };

  watch(selectedYear, async () => {
    if (props.modelValue && props.userId) {
      await fetchBalance();
    }
  });

  watch(
    [() => props.modelValue, () => props.userId],
    async ([isOpen, userId]) => {
      if (isOpen && userId) {
        await fetchBalance();
      }
    },
    { immediate: true },
  );

  /* ============================= */
  /* UPDATE BALANCE */
  /* ============================= */
  const submitEditBalance = async () => {
    if (!editingBalance.value) return;

    if (editingBalance.value.newTotal < editingBalance.value.used) {
      showToast("error", "New total cannot be less than used leaves!");
      return;
    }

    try {
      isLoading.value = true;
      await store.dispatch("updateLeaveBalance", {
        leaveType: editingBalance.value.type,
        total: editingBalance.value.newTotal,
        userId: props.userId,
      });

      await fetchBalance();
      showEditModal.value = false;
      editingBalance.value = null;
      showToast("success", "Leave quota updated successfully!");
    } catch (err) {
      console.error("Failed to update balance", err);
      showToast("error", "Failed to update leave balance");
    } finally {
      isLoading.value = false;
    }
  };

  /* ============================= */
  /* DELETE LEAVE TYPE */
  /* ============================= */
  const requestDeleteLeaveType = (item) => {
    if (item.used > 0) {
      showToast(
        "error",
        `Cannot delete ${item.type} because ${item.used} leave(s) have already been used.`,
      );
      return;
    }

    confirmConfig.title = "Delete Leave Type";
    confirmConfig.description = `Are you sure you want to permanently delete "${item.type}"? This action cannot be undone.`;
    confirmConfig.buttonText = "Delete";
    confirmConfig.isDestructive = true;

    confirmConfig.action = async () => {
      confirmLoading.value = true;
      try {
        await store.dispatch("deleteLeaveType", item.type);
        await fetchBalance();
        showToast("success", "Leave type deleted successfully!");
        showConfirmModal.value = false;
      } catch (err) {
        showToast(
          "error",
          err.response?.data?.error || "Failed to delete leave type.",
        );
      } finally {
        confirmLoading.value = false;
      }
    };

    showConfirmModal.value = true;
  };

  /* ============================= */
  /* PRODUCTION CHART LOGIC */
  /* ============================= */
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const colorPalette = {
    Casual: "#6366F1", // Indigo
    Personal: "#8B5CF6", // Violet
    Sick: "#F43F5E", // Rose
    Earned: "#F59E0B", // Amber
    Unpaid: "#64748B", // Slate
    Maternity: "#06B6D4", // Cyan
    Paternity: "#0EA5E9", // Sky
  };

  const dynamicPalette = ["#6366F1", "#10B981", "#F59E0B", "#F43F5E", "#8B5CF6", "#06B6D4", "#EC4899"];

  const getColorForLeaveType = (leaveType, index = 0) => {
    if (colorPalette[leaveType]) return colorPalette[leaveType];
    return dynamicPalette[index % dynamicPalette.length];
  };

  const activeLeaveTypes = computed(() => {
    const map = {};
    if (!leaveBalance.value?.balances) return map;
    leaveBalance.value.balances.forEach((item, index) => {
      map[item.type] = getColorForLeaveType(item.type, index);
    });
    return map;
  });

  const monthlyChartData = computed(() => {
    if (!leaveBalance.value?.balances?.length) return [];
    return leaveBalance.value.monthlyData || [];
  });

  const initializeChart = async () => {
    await nextTick();
    if (!chartCanvas.value) return;

    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }

    const isDark = document.documentElement.classList.contains("dark");
    const tickColor = isDark ? "#94A3B8" : "#64748B";
    const gridColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)";

    // Determine leave types from balances or monthlyData
    const leaveTypes = leaveBalance.value?.balances?.map((b) => b.type) || [];
    if (leaveTypes.length === 0) return;

    const datasets = leaveTypes.map((leaveType, index) => {
      const color = getColorForLeaveType(leaveType, index);

      // Build data points
      const data = months.map((month) => {
        const found = monthlyChartData.value.find(
          (item) => item.month === month && item.type === leaveType,
        );
        return found ? found.count : 0;
      });

      return {
        label: leaveType,
        data,
        backgroundColor: color,
        hoverBackgroundColor: color,
        borderRadius: 6,
        borderSkipped: false,
        stack: "leaves",
        maxBarThickness: 32,
      };
    });

    chartInstance = new Chart(chartCanvas.value, {
      type: "bar",
      data: {
        labels: months,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        devicePixelRatio: Math.min(window.devicePixelRatio || 2, 2),
        animation: {
          duration: 750,
          easing: "easeOutQuart",
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: {
            display: false, // We render clean HTML legend pills above
          },
          tooltip: {
            backgroundColor: isDark ? "rgba(17, 28, 48, 0.95)" : "rgba(15, 23, 42, 0.95)",
            titleColor: "#FFFFFF",
            bodyColor: "#CBD5E1",
            footerColor: "#38BDF8",
            borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.15)",
            borderWidth: 1,
            padding: 12,
            cornerRadius: 12,
            boxPadding: 4,
            usePointStyle: true,
            callbacks: {
              title: (context) => `${context[0].label} ${selectedYear.value}`,
              label: (context) => `  ${context.dataset.label}: ${context.parsed.y} day(s)`,
              footer: (tooltipItems) => {
                const total = tooltipItems.reduce((sum, item) => sum + (item.parsed.y || 0), 0);
                return total > 0 ? `Total: ${total} leaves` : "No leaves logged";
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              color: tickColor,
              font: {
                size: 12,
                weight: "600",
                family: "inherit",
              },
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: {
              color: gridColor,
              borderDash: [4, 4],
              drawBorder: false,
              drawTicks: false,
            },
            ticks: {
              stepSize: 1,
              precision: 0,
              color: tickColor,
              font: {
                size: 11,
                weight: "500",
                family: "inherit",
              },
              padding: 8,
            },
            border: {
              display: false,
            },
          },
        },
      },
    });
  };

  const updateChart = async () => {
    await initializeChart();
  };

  watch(
    () => leaveBalance.value,
    async () => {
      await updateChart();
    },
    { deep: true },
  );

  const handleClickOutside = (event) => {
    if (modalRef.value && !modalRef.value.contains(event.target)) {
      activeMenu.value = null;
    }
  };

  onMounted(() => {
    document.addEventListener("click", handleClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }
  });
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 180ms cubic-bezier(0.4, 0, 0.2, 1),
      transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: scale(0.98);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: scale(1);
  }
</style>
